import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Promotion {
  id: number;
  title: string;
  body: string;
  audience: "nurse" | "patient" | "both";
  ctaLabel: string | null;
  ctaUrl: string | null;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type PForm = {
  title: string;
  body: string;
  audience: "nurse" | "patient" | "both";
  ctaLabel: string;
  ctaUrl: string;
  isActive: boolean;
  startsAt: string;
  endsAt: string;
};

const EMPTY: PForm = {
  title: "", body: "", audience: "both", ctaLabel: "", ctaUrl: "", isActive: true, startsAt: "", endsAt: "",
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? "Request failed"); }
  return res.json();
}

function promToForm(p: Promotion): PForm {
  return {
    title: p.title,
    body: p.body,
    audience: p.audience,
    ctaLabel: p.ctaLabel ?? "",
    ctaUrl: p.ctaUrl ?? "",
    isActive: p.isActive,
    startsAt: p.startsAt ? p.startsAt.slice(0, 16) : "",
    endsAt: p.endsAt ? p.endsAt.slice(0, 16) : "",
  };
}

function formToBody(f: PForm) {
  return {
    title: f.title,
    body: f.body,
    audience: f.audience,
    ctaLabel: f.ctaLabel || null,
    ctaUrl: f.ctaUrl || null,
    isActive: f.isActive,
    startsAt: f.startsAt || null,
    endsAt: f.endsAt || null,
  };
}

function PForm({ value, onChange }: { value: PForm; onChange: (v: PForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <div>
        <Label>Title *</Label>
        <Input value={value.title} onChange={(e) => set("title", e.target.value)} placeholder="Banner headline" />
      </div>
      <div>
        <Label>Body *</Label>
        <Textarea rows={3} value={value.body} onChange={(e) => set("body", e.target.value)} placeholder="Banner message shown to users…" />
      </div>
      <div>
        <Label>Audience *</Label>
        <Select value={value.audience} onValueChange={(v) => set("audience", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="nurse">Nurses only</SelectItem>
            <SelectItem value="patient">Patients only</SelectItem>
            <SelectItem value="both">Both</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>CTA label</Label>
          <Input value={value.ctaLabel} onChange={(e) => set("ctaLabel", e.target.value)} placeholder="Learn more" />
        </div>
        <div>
          <Label>CTA URL</Label>
          <Input value={value.ctaUrl} onChange={(e) => set("ctaUrl", e.target.value)} type="url" placeholder="https://…" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Starts at</Label>
          <Input type="datetime-local" value={value.startsAt} onChange={(e) => set("startsAt", e.target.value)} />
        </div>
        <div>
          <Label>Ends at</Label>
          <Input type="datetime-local" value={value.endsAt} onChange={(e) => set("endsAt", e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={value.isActive} onCheckedChange={(v) => set("isActive", v)} />
        <Label>Active</Label>
      </div>
    </div>
  );
}

const audienceColors: Record<string, string> = {
  nurse: "bg-teal-100 text-teal-800",
  patient: "bg-blue-100 text-blue-800",
  both: "bg-purple-100 text-purple-800",
};

export default function AdminPromotions() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [form, setForm] = useState<PForm>(EMPTY);

  const { data: promotions = [], isLoading } = useQuery<Promotion[]>({
    queryKey: ["admin", "promotions"],
    queryFn: () => apiFetch("/api/admin/content/promotions"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: PForm) =>
      editing
        ? apiFetch(`/api/admin/content/promotions/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(formToBody(body)) })
        : apiFetch("/api/admin/content/promotions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(formToBody(body)) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "promotions"] }); setDialogOpen(false); toast({ title: editing ? "Promotion updated" : "Promotion created" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/promotions/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "promotions"] }); toast({ title: "Promotion deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(p: Promotion) { setEditing(p); setForm(promToForm(p)); setDialogOpen(true); }

  function isLive(p: Promotion): boolean {
    if (!p.isActive) return false;
    const now = Date.now();
    if (p.startsAt && new Date(p.startsAt).getTime() > now) return false;
    if (p.endsAt && new Date(p.endsAt).getTime() < now) return false;
    return true;
  }

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Promotions"
        description="Dismissible banners shown on nurse and patient dashboards"
        action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New promotion</Button>}
      />

      {promotions.length === 0 && !isLoading && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 flex items-start gap-4 mb-6">
          <Megaphone className="h-5 w-5 text-teal-700 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-teal-900">No promotions yet</p>
            <p className="text-sm text-teal-700 mt-0.5">Create a promotion to display a dismissible banner on nurse or patient dashboards.</p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Title", "Audience", "Schedule", "Status", "Actions"]}
          rows={promotions.map((p) => [
            <div key="title">
              <p className="font-medium text-slate-900">{p.title}</p>
              <p className="text-xs text-slate-500 line-clamp-1">{p.body}</p>
            </div>,
            <span key="aud" className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${audienceColors[p.audience] ?? "bg-slate-100 text-slate-700"}`}>{p.audience}</span>,
            <div key="sched" className="text-xs text-slate-500">
              {p.startsAt && <p>From {new Date(p.startsAt).toLocaleDateString("en-GB")}</p>}
              {p.endsAt && <p>Until {new Date(p.endsAt).toLocaleDateString("en-GB")}</p>}
              {!p.startsAt && !p.endsAt && <p>No schedule</p>}
            </div>,
            <span key="status" className={`text-xs px-2 py-0.5 rounded-full font-medium ${isLive(p) ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{isLive(p) ? "Live" : "Inactive"}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(p.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Edit promotion" : "New promotion"}</DialogTitle></DialogHeader>
          <PForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete promotion?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the promotional banner.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
